import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { TID } from '../../constants/testIds';
import { BellRing, Bell, X } from 'lucide-react';

const STORAGE_KEY = 'bn_watch_email';

export default function WatchlistDashboard({ userEmail }) {
    const [email, setEmail] = useState(() => userEmail || (typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : '') || '');
    const [items, setItems] = useState([]);
    const [alertsCount, setAlertsCount] = useState(0);
    const [loaded, setLoaded] = useState(false);

    async function load(currentEmail) {
        if (!currentEmail) { setLoaded(true); return; }
        try {
            const { data } = await api.get('/watchlist/status', { params: { email: currentEmail } });
            setItems(data.items || []);
            setAlertsCount(data.alerts_count || 0);
        } finally {
            setLoaded(true);
        }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(email); }, []);

    async function remove(watch_id) {
        await api.delete(`/watchlist/${watch_id}`, { params: { email } });
        setItems((s) => s.filter((i) => i.watch_id !== watch_id));
    }

    return (
        <section className="border-t border-neutral-200">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="overline">Watchlist</div>
                        {alertsCount > 0 && (
                            <span data-testid={TID.watch.alertPill} className="text-[11px] uppercase tracking-[0.18em] px-2.5 py-1 border border-[#2F6B47] text-[#2F6B47] bg-white inline-flex items-center gap-1.5">
                                <BellRing className="w-3 h-3" /> {alertsCount} pending alert{alertsCount === 1 ? '' : 's'}
                            </span>
                        )}
                    </div>
                    {!userEmail && (
                        <form onSubmit={(e) => { e.preventDefault(); localStorage.setItem(STORAGE_KEY, email); load(email); }} className="flex items-center gap-2">
                            <input
                                type="email"
                                placeholder="Enter your watchlist email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="border border-neutral-300 px-3 py-2 text-[13px] w-64"
                               
                            />
                            <button className="border border-brand-navy bg-brand-navy text-white px-3 py-2 text-[13px] hover-invert hover:bg-brand-gold hover:text-brand-navy">Load</button>
                        </form>
                    )}
                </div>

                {!loaded ? (
                    <div className="text-neutral-500">Loading…</div>
                ) : items.length === 0 ? (
                    <div className="border border-neutral-200 p-10 text-neutral-500">
                        <div className="flex items-center gap-2 mb-2 overline">
                            <Bell className="w-3.5 h-3.5" /> No alerts set
                        </div>
                        <div className="text-[14px]">
                            Open any <Link to="/neighbourhoods" className="link-editorial text-black">neighbourhood</Link> and use “Alert me” to be notified when its numbers move.
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {items.map((i) => (
                            <div key={i.watch_id} data-testid={TID.watch.listItem} className={`border p-5 bg-white ${i.has_alert ? 'border-[#2F6B47]' : 'border-neutral-200'}`}>
                                <div className="flex items-start justify-between">
                                    <div>
                                        <div className="overline">{i.city} · {i.province}</div>
                                        <Link to={`/neighbourhoods/${i.slug}`} className="font-editorial text-[22px] tracking-editorial mt-1 block link-editorial">{i.name}</Link>
                                    </div>
                                    <button
                                        data-testid={TID.watch.remove}
                                        onClick={() => remove(i.watch_id)}
                                        aria-label="Remove"
                                        className="text-neutral-400 hover:text-black"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                                <div className="mt-4 grid grid-cols-3 gap-0 border-l border-t border-neutral-200">
                                    <Delta label="Price" delta={i.price_delta_pct} unit="%" />
                                    <Delta label="1Y" delta={i.y1_delta_pts} unit="pp" />
                                    <Delta label="Forecast" delta={i.forecast_delta_pts} unit="pp" />
                                </div>
                                {i.has_alert && (
                                    <div className="mt-3 text-[12px] text-[#2F6B47] flex items-center gap-1.5">
                                        <BellRing className="w-3.5 h-3.5" />
                                        {i.triggered.map((t) => t.type.replace('_', ' ')).join(', ')} moved past threshold ±{i.threshold_pct}%
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}

function Delta({ label, delta, unit }) {
    const positive = delta > 0;
    const zero = delta === 0;
    const color = zero ? 'text-neutral-500' : positive ? 'text-[#0B8A3E]' : 'text-[#C3341C]';
    return (
        <div className="border-r border-b border-neutral-200 p-3">
            <div className="overline">{label}</div>
            <div className={`font-mono-data text-[15px] tabular mt-1 ${color}`}>
                {zero ? '—' : `${positive ? '+' : ''}${delta.toFixed(2)}${unit}`}
            </div>
        </div>
    );
}
